import React, { useContext, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Url } from '@wix/da-url';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import TagList from '@wix/da-ds/pkg/TagList';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import CheckIcon from '@wix/da-ds/pkg/Icons/Check';
import ModalFooterMobile from '@wix/da-ds/pkg/modals/dsPresets/ModalFooterMobile';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import Textarea from '@wix/da-shared-react/pkg/Textarea';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { SubmitFormContext } from '../../context';
import { useSubmitField } from '../../hooks/useSubmitField';
import { initAdapter, submitAdapter } from './adapters';
import { withConditionalField } from '../../hoc/withConditionalField';

import s from './DreamupPromptField.scss';

const DreamupPromptField: React.FC<{}> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { watch, setValue } = useFormContext();
  const { initResponse, pubsub } = useContext(SubmitFormContext)!;
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const userCoreLevel = useSelector(getCoreLevel);
  const prompt = initResponse?.deviation?.extended?.dreamsofart?.prompt;
  const labels = watch('labels');

  const {
    id,
    field: { onChange, value },
  } = useSubmitField('promptHidden', {
    initAdapter,
    submitAdapter,
  });

  const onToggleHidePrompt = useCallback(
    val => {
      if (
        userCoreLevel === 'core_pro' ||
        userCoreLevel === 'core_pro_plus' ||
        userCoreLevel === 'core_max'
      ) {
        onChange(val);
      } else {
        setValue('promptHidden', false, { shouldDirty: true });
        setShowUpsellModal(true);
      }
    },
    [userCoreLevel, onChange, setValue, setShowUpsellModal]
  );
  const onModalDismiss = useCallback(() => {
    setShowUpsellModal(false);
  }, [setShowUpsellModal]);

  const onDetailsLinkClick = useCallback(() => {
    pubsub.publish('labels', 'toggle');
  }, [pubsub]);

  const filteredLabels = (labels || []).filter(label => label.type === 'aireference');
  const tagList = filteredLabels.length > 0 && (
    <TagList
      className={s['labels']}
      tags={filteredLabels.map(label => ({
        tagname: t('deviationSubmit.form.labels.tag.aireference', {
          value: label.name,
        }),
      }))}
      size="small"
      multiline
    />
  );

  const promptId = 'dreamup-prompt-text';

  return (
    <div className={s['root']}>
      <Textarea
        id={promptId}
        label={t('deviationSubmit.form.dreamup.prompt.label')}
        textareaClassName={s['prompt-textarea']}
        value={prompt}
        disabled
        noBottomSpacing
      />

      <ButtonFieldLayout
        label={
          <span className={s['hide-prompt-label']}>
            {t('deviationSubmit.form.dreamup.hidePrompt.label')}
            <CoreSymbol size={IconSize.SMALLER} corePackage="core_plus" disableDefaultColors />
          </span>
        }
        labelProps={{ id: `${id}-label`, htmlFor: id }}
      >
        <MainToggle
          variant="dreamup-purple"
          id={id}
          size={isMobile ? 'large' : 'small'}
          value={value}
          onChange={onToggleHidePrompt}
          aria-labelledby={`${id}-label`}
        />
      </ButtonFieldLayout>
      {showUpsellModal && (
        <Modal
          isOpen
          className={s['hide-prompt-modal']}
          heading={
            <div className={s['hide-prompt-heading']}>
              {t('deviationSubmit.form.dreamup.hidePrompt.modal.title')}
            </div>
          }
          hasCloseButton
          onRequestClose={onModalDismiss}
          footer={
            <ModalFooterMobile
              className={s['hide-prompt-modal-footer']}
              primaryButton={
                <BuyCoreBrandedButton
                  size="large"
                  referer={Url.dreamupLink()}
                  withMobileDefaultLabel={false}
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.HIDE_PROMPT,
                  })}
                >
                  {t('deviationSubmit.form.dreamup.hidePrompt.modal.cta')}
                </BuyCoreBrandedButton>
              }
            />
          }
        >
          <ul>
            <li>
              <CheckIcon
                className={s['hide-prompt-modal-benefit-icon']}
                size={IconSize.TINY}
                disableDefaultColors
              />
              {t('deviationSubmit.form.dreamup.hidePrompt.modal.benefits.create')}
            </li>
            <li>
              <CheckIcon
                className={s['hide-prompt-modal-benefit-icon']}
                size={IconSize.TINY}
                disableDefaultColors
              />
              <Trans
                i18nKey={'deviationSubmit.form.dreamup.hidePrompt.modal.benefits.hide.core_max'}
              >
                Hide prompts with
                <UserSymbol userSymbol="core_pro" />
                <UserSymbol userSymbol="core_pro_plus" />
                <UserSymbol userSymbol="core_max" />
              </Trans>
            </li>
            <li>
              <CheckIcon
                className={s['hide-prompt-modal-benefit-icon']}
                size={IconSize.TINY}
                disableDefaultColors
              />
              {t('deviationSubmit.form.dreamup.hidePrompt.modal.benefits.start')}
            </li>
          </ul>
        </Modal>
      )}

      <ButtonFieldLayout
        label={t('deviationSubmit.form.dreamup.reference.label')}
        bottomSlot={tagList}
      >
        <TextButton variant="main" size="small" onClick={onDetailsLinkClick}>
          {filteredLabels.length > 0
            ? t('deviationSubmit.form.labels.button.edit')
            : t('deviationSubmit.form.labels.button.add')}
        </TextButton>
      </ButtonFieldLayout>
    </div>
  );
};
DreamupPromptField.displayName = 'DreamupPromptField';

// Field only renders when it's a dreamup deviation
export default withConditionalField<{}>(
  DreamupPromptField,
  ({ deviation }) => !!deviation?.extended?.dreamsofart
);
